import type { CheckoutV2Configuration } from "@jti/commerce";
import type { ShippingFormFields, TelephoneFieldValue } from "@jti/commerce-types";
import type { Address, AddressStored } from "@jti/fe-common";

import { telephoneFieldFactory } from "@checkout-v2/formFields";
import { shippingMethodFieldFactory } from "@checkout-v2/formFields/factories/shippingMethodCodeFieldFactory";
import type { RawAddressWithNoContactInfo } from "@checkout-v2/shared/types/RawAddressWithNoContactInfo.types";
import {
  getMagentoAddressFromCustomDelivery,
  getMagentoAddressFromInStoreDelivery,
} from "@checkout-v2/shared/utils/getMagentoAddress";

const enrichAddressWithContactData = (
  address: Address | RawAddressWithNoContactInfo,
  data: ShippingFormFields,
  telephoneFallback?: TelephoneFieldValue
): Address => ({
  ...address,
  firstname: data.firstName,
  lastname: data.lastName,
  telephone: telephoneFieldFactory.toTelephoneString(data.telephone, telephoneFallback),
});

export const constructBillingAddressPayload = (
  data: ShippingFormFields,
  checkoutConfiguration: CheckoutV2Configuration,
  isBillingAddressVisibleForCheckout: boolean,
  telephoneFallback?: TelephoneFieldValue,
  defaultBillingAddress?: AddressStored | null
): Address => {
  const shippingMethodType = shippingMethodFieldFactory.getSelectedShippingMethodType(
    data.shippingMethodCode,
    checkoutConfiguration
  );

  const isAddressHomeDeliveryData = data?.addressHomeDelivery && !!Object.keys(data.addressHomeDelivery).length;

  if (!shippingMethodType) {
    throw new Error("Shipping method type is not available");
  }

  if (!isBillingAddressVisibleForCheckout) {
    if (shippingMethodType !== "atHome" && data.billingAddress === null) {
      const deliveryAddressResolvers = {
        "custom delivery": {
          address: getMagentoAddressFromCustomDelivery(data.addressCustomShipping),
          errorMessage: "Custom shipping delivery address is not available for billing",
        },
        "in-store delivery": {
          address: getMagentoAddressFromInStoreDelivery(data.addressInStoreDelivery),
          errorMessage: "Store shipping delivery address is not available for billing",
        },
      } as const;

      const deliveryAddressResolver = deliveryAddressResolvers[shippingMethodType];

      if (!deliveryAddressResolver?.address) {
        throw new Error(deliveryAddressResolver.errorMessage);
      }

      return enrichAddressWithContactData(deliveryAddressResolver.address, data, telephoneFallback);
    }

    if (!isAddressHomeDeliveryData) {
      throw new Error("Home delivery address is not available for billing");
    }

    return enrichAddressWithContactData(data.addressHomeDelivery, data, telephoneFallback);
  }

  // For atHome with "same as shipping" checked - use shipping address
  if (shippingMethodType === "atHome" && data.isBillingAddressSameAsShipping && data.addressHomeDelivery) {
    return enrichAddressWithContactData(data.addressHomeDelivery, data, telephoneFallback);
  }

  // Non-atHome shows the default billing address read-only without syncing it to the form, so fall back to it here
  const billingAddress =
    data.billingAddress ??
    (shippingMethodType !== "atHome" && defaultBillingAddress?.isDefaultBilling ? defaultBillingAddress : null);

  if (!billingAddress) {
    throw new Error("Billing address is not provided");
  }

  // For all other cases - use the billing address
  return enrichAddressWithContactData(billingAddress, data, telephoneFallback);
};
